import { getZIndex } from "@dhx/ts-message";
import { Chart } from "./Chart";
import { calcPointRef } from "./helpers/common";
import { getShadeHTMLHelper } from "./shapes/line";
import { ChartEvents } from "./types";

export class Tooltip {
	private _chart: Chart;
	private _prevLine: HTMLElement | null;
	private _mouseOverBar: boolean;
	private _tooltip: HTMLElement;

	constructor(chart) {
		this._chart = chart;
		this._initEvents();
	}

	destructor() {
		if (this._tooltip) {
			document.body.removeChild(this._tooltip);
			this._tooltip = null;
		}
	}

	_showLineTooltip(lineTooltipItems) {
		let lineTooltip = "";
		const root = this._chart.getRootView();
		let calcPointTop = 0;
		for (const item of lineTooltipItems) {
			calcPointTop += item.top;
			this._prevLine =
				root &&
				root.refs &&
				root.refs["line" + Math.round(item.left)] &&
				root.refs["line" + Math.round(item.left)].el;
			this._prevLine.classList.add("grid-line__active");
			if (!item.text) continue;

			const seria = this._chart.getSeries(item.seriaId);
			const point = seria.getPoints();
			if (!point.length || !seria.config.tooltip) continue;
			const shadeColor =
				seria.config.pointColor ||
				(seria.config.color && seria.config.color !== "none"
					? seria.config.color
					: seria.config.fill);
			const shadeHTMLHelper = getShadeHTMLHelper(
				seria.config.pointType !== "empty" && seria.config.pointType
					? seria.config.pointType
					: "simpleRect",
				shadeColor
			);
			lineTooltip += `<div class="line-point" _ref="dhx_tooltip_${seria.config.id}_box">
				<svg class="dhx_tooltip_svg" role="graphics-document" style="width: 8px; height: 8px;">${shadeHTMLHelper(
					4,
					4,
					point[0][2]
				)}</svg>
				<span class="dhx_line-point-text" _ref="dhx_tooltip_${seria.config.id}_text">${item.text}</span>
			</div>`;
		}
		if (!lineTooltip) return;
		if (!this._tooltip) {
			this._createTooltip();
		}

		const bodyHeight = document.body.offsetHeight;
		const bodyWidth = document.body.offsetWidth;

		this._tooltip.innerHTML = lineTooltip;
		this._tooltip.classList.add("dhx_chart_tooltip__visible");

		const middlePointHeight = calcPointTop / lineTooltipItems.length;
		const lineRect = this._prevLine.getBoundingClientRect();
		const contentHight = this._tooltip.offsetHeight;
		const contentWidth = this._tooltip.offsetWidth;
		let top = middlePointHeight - contentHight / 2 + lineRect.top + window.scrollY;
		let left = lineRect.left + 10;
		if (top + contentHight > bodyHeight) {
			top -= top + contentHight - bodyHeight;
		}

		if (left + contentWidth > bodyWidth) {
			left = lineRect.left - contentWidth - 10;
		}
		const zIndex = getZIndex(this._chart.getRootNode());
		if (zIndex) {
			this._tooltip.style.zIndex = zIndex.toString();
		}
		this._tooltip.style.left = left + "px";
		this._tooltip.style.top = top + "px";
	}

	_showTooltip(text, e: MouseEvent) {
		if (!this._tooltip) {
			this._createTooltip();
		}

		const bodyHeight = document.body.offsetHeight;
		const bodyWidth = document.body.offsetWidth;
		this._tooltip.innerHTML = text;
		this._tooltip.classList.add("dhx_chart_tooltip__visible");
		const contentHight = this._tooltip.offsetHeight;
		const contentWidth = this._tooltip.offsetWidth;
		let mouseTop = e.pageY + 10;
		let mouseLeft = e.pageX + 10;

		if (mouseTop + contentHight > bodyHeight) {
			mouseTop = e.pageY - contentHight;
		}

		if (mouseLeft + contentWidth > bodyWidth) {
			mouseLeft = e.pageX - contentWidth - 10;
		}
		const zIndex = getZIndex(this._chart.getRootNode());
		if (zIndex) {
			this._tooltip.style.zIndex = zIndex.toString();
		}
		this._tooltip.style.left = mouseLeft + "px";
		this._tooltip.style.top = mouseTop + "px";
	}

	_showTooltipOnClosest(closest) {
		if (!this._tooltip) {
			this._createTooltip();
		}
		const tooltipSeries = this._chart.getSeries(closest[4]);
		if (tooltipSeries) {
			const text = tooltipSeries.getTooltipText(closest[3]);
			if (!text) return;
			const layersSize = (this._chart as any)._layers.getSizes();
			const root = this._chart.getRootNode();
			const rect = root.getBoundingClientRect();
			this._tooltip.innerHTML = text;
			this._tooltip.classList.add("dhx_chart_tooltip__visible");
			const bodyHeight = document.body.offsetHeight;
			const contentHight = this._tooltip.offsetHeight;

			let top =
				closest[2] +
				rect.top +
				contentHight / 2 +
				layersSize.top -
				(this._chart.config.type === "radar" ? 10 : 15);

			if (top + contentHight > bodyHeight) {
				top =
					closest[2] +
					rect.top -
					contentHight / 2 +
					layersSize.top -
					(this._chart.config.type === "radar" ? 10 : 15);
			}
			if (top + contentHight > bodyHeight) {
				this._tooltip.classList.remove("dhx_chart_tooltip__visible");
			}
			const zIndex = getZIndex(this._chart.getRootNode());
			if (zIndex) {
				this._tooltip.style.zIndex = zIndex.toString();
			}
			this._tooltip.style.left = rect.left + closest[1] + 10 + "px";
			this._tooltip.style.top = top + "px";
		}
	}

	_createTooltip() {
		this._tooltip = document.createElement("div");
		this._tooltip.setAttribute("data-dhx-widget-id", (this._chart as any)._uid);
		this._tooltip.classList.add(
			"dhx_chart_tooltip",
			"dhx_chart_tooltip_line",
			"dhx_chart_tooltip__hidden",
			"tooltip-text"
		);
		document.body.appendChild(this._tooltip);
	}
	private _initEvents(): void {
		this._chart.events.on(ChartEvents.chartMouseMove, (x, y) => {
			if (this._prevLine) {
				this._prevLine.classList.remove("grid-line__active");
			}
			if (!this._mouseOverBar) {
				const lineTooltipItems = [];
				const closest: [number, number, number, string, string] = [Infinity, null, null, null, null]; // (dist, x, y, id, serieid)
				this._chart.eachSeries(seria => {
					const type = seria.config.type;
					if (type === "line" || type === "spline" || type === "splineArea" || type === "area") {
						const closestLine = seria.getClosestVertical(x);
						const item = {
							value: closestLine[4],
							text: seria.getTooltipText(closestLine[3]),
							seriaId: seria.config.id,
							left: closestLine[1],
							top: closestLine[2],
						};
						if (typeof item.left === "number" && typeof item.top === "number") {
							lineTooltipItems.push(item);
						}
					} else if (type === "pie" || type === "pie3D" || type === "donut" || type === "radar") {
						const seriaClosest = seria.getClosest(x, y);
						if (closest[0] > seriaClosest[0]) {
							closest[0] = seriaClosest[0];
							closest[1] = seriaClosest[1];
							closest[2] = seriaClosest[2];
							closest[3] = seriaClosest[3];
							closest[4] = seria.id;
						}
					}
				});
				if (lineTooltipItems.length) {
					lineTooltipItems.sort((a, b) => b.value - a.value);
					this._showLineTooltip(lineTooltipItems);
				} else {
					this._showTooltipOnClosest(closest);
				}
			}
		});

		this._chart.events.on(ChartEvents.seriaMouseMove, (id, seriaId, e) => {
			this._mouseOverBar = true;
			const rootView = this._chart.getRootView();
			const seria = this._chart.getSeries(seriaId);
			const text = seria.getTooltipText(id);
			const item = rootView && rootView.refs && rootView.refs[calcPointRef(id, seriaId)].el;
			item?.setAttribute("fill-opacity", seria.config.alpha > 0.6 ? seria.config.alpha - 0.4 : 1);
			if (text) {
				this._showTooltip(text, e);
			} else if (this._tooltip) {
				this._tooltip.classList.remove("dhx_chart_tooltip__visible");
			}
		});

		this._chart.events.on(ChartEvents.seriaMouseLeave, (id, seriaId) => {
			this._mouseOverBar = false;
			const rootView = this._chart.getRootView();
			const seria = this._chart.getSeries(seriaId);
			if (seria.config.type !== "area" && seria.config.type !== "treeMap") {
				const item = rootView && rootView.refs && rootView.refs[calcPointRef(id, seriaId)].el;
				item.setAttribute("fill-opacity", seria.config.alpha);
			}
			if (this._tooltip) {
				this._tooltip.classList.remove("dhx_chart_tooltip__visible");
			}
		});
		this._chart.events.on(ChartEvents.chartMouseLeave, () => {
			if (this._tooltip) {
				this._tooltip.classList.remove("dhx_chart_tooltip__visible");
			}
			if (this._prevLine) {
				this._prevLine.classList.remove("grid-line__active");
			}
		});
	}
}
